import React, { useState, useMemo } from 'react';
import { ArrowUpDown } from 'lucide-react';

const sym = (c) => (c === 'inr' ? '₹' : '$');
export const priceLabel = (p) => {
  if (p.pricing_type === 'per_day') return `${sym(p.currency)}${p.per_day_price}/day`;
  if (p.pricing_type === 'flat_or_per_day') return `${sym(p.currency)}${p.price} / ${sym(p.currency)}${p.per_day_price}/day`;
  if (p.pricing_type === 'tickets') return (p.ticket_types || []).length ? `From ${sym(p.currency)}${Math.min(...p.ticket_types.map((t) => t.price || 0))}` : 'Tickets';
  return p.is_free ? 'Free' : `${sym(p.currency)}${p.price}`;
};
const dateVal = (p) => {
  if (p.start_datetime) return new Date(p.start_datetime).getTime();
  return 0;
};
const dateLabel = (p) => {
  if (p.type === 'class' && p.time_of_day) return `Class · ${p.time_of_day}`;
  if (p.start_datetime) { try { return new Date(p.start_datetime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return '—'; } }
  return '—';
};

const statusBadge = {
  approved: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700', draft: 'bg-mahogany/10 text-mahogany',
};

const COLS = [
  { key: 'title', label: 'Title' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'price', label: 'Price' },
];

// Dense, sortable program table. `renderActions(program)` returns the row action buttons.
const ProgramTable = ({ rows, renderActions }) => {
  const [sort, setSort] = useState({ key: 'date', dir: 'asc' });

  const sorted = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let av; let bv;
      if (key === 'date') { av = dateVal(a); bv = dateVal(b); }
      else if (key === 'price') { av = a.is_free ? 0 : (a.pricing_type === 'per_day' ? a.per_day_price : a.price) || 0; bv = b.is_free ? 0 : (b.pricing_type === 'per_day' ? b.per_day_price : b.price) || 0; }
      else { av = `${a[key] || ''}`.toLowerCase(); bv = `${b[key] || ''}`.toLowerCase(); }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  if (rows.length === 0) {
    return <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">No programs match your filters.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-rose/40 bg-white/70">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-mahogany/70 border-b border-rose/40">
            {COLS.map((c) => (
              <th key={c.key} className="px-4 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(c.key)}>
                <span className="inline-flex items-center gap-1">{c.label} <ArrowUpDown className={`w-3 h-3 ${sort.key === c.key ? 'text-mahogany' : 'text-mahogany/30'}`} /></span>
              </th>
            ))}
            <th className="px-4 py-3 font-medium">Flags</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.id} className="border-b border-rose/20 last:border-0 hover:bg-rose-soft/20">
              <td className="px-4 py-3 max-w-[280px]">
                <div className="font-medium text-burgundy truncate">{p.title}</div>
                {p.rejection_reason && <div className="text-xs text-red-600 truncate">Rejected: {p.rejection_reason}</div>}
              </td>
              <td className="px-4 py-3 capitalize text-burgundy/80">{p.type}</td>
              <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${statusBadge[p.status] || ''}`}>{p.status}</span></td>
              <td className="px-4 py-3 text-burgundy/80 whitespace-nowrap">{dateLabel(p)}</td>
              <td className="px-4 py-3 text-burgundy/80 whitespace-nowrap">{priceLabel(p)}</td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  {p.is_published ? <span title="Published" className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">Live</span> : <span title="Draft" className="text-[10px] px-2 py-0.5 rounded-full bg-mahogany/10 text-mahogany">Draft</span>}
                  {p.is_featured && <span title="Featured" className="text-[10px] px-2 py-0.5 rounded-full bg-mahogany text-cream">★</span>}
                </div>
              </td>
              <td className="px-4 py-3"><div className="flex justify-end gap-1.5">{renderActions(p)}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProgramTable;
